import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, Trash2, Settings, Eye, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { notificationAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const { isAuthenticated } = useAuth();
  
  // Early return BEFORE other hooks
  if (!isAuthenticated) {
    return null;
  }
  
  const { 
    notifications, 
    unreadCount, 
    isConnected, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    fetchNotifications 
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleNotificationView = async (notification) => {
    try {
      // Debug logging
      console.log('NotificationBell: View button clicked for notification:', notification);
      console.log('NotificationBell: Action data:', notification.action_data);
      console.log('NotificationBell: Navigate function available:', typeof navigate);
      
      // Try to mark as read, but don't let it block navigation
      if (notification.status === 'unread') {
        try {
          await markAsRead(notification.id);
        } catch (readError) {
          console.warn('NotificationBell: Failed to mark as read, but continuing with navigation:', readError);
        }
      }

      // Navigate based on action_data
      if (notification.action_data && notification.action_data.action) {
        const { action, conversation_id, user_id, circle_id, post_id } = notification.action_data;
        console.log('NotificationBell: Navigating with action:', action, {conversation_id, user_id, circle_id, post_id});
        
        switch (action) {
          case 'view_conversation':
            if (conversation_id) {
              // Navigate to messages with conversation selected
              navigate(`/profile/edit?tab=inbox&conversation=${conversation_id}`);
            } else {
              // Fallback to messages tab
              navigate('/profile/edit?tab=inbox');
            }
            break;
            
          case 'view_profile':
            if (user_id) {
              navigate(`/profile/${user_id}`);
            }
            break;
            
          case 'view_circle':
            // Community Circles feature has been removed, navigate to explore
            navigate('/explore');
            break;
            
          case 'view_post':
            // Community Circles feature has been removed, navigate to explore
            navigate('/explore');
            break;
            
          default:
            console.warn('Unknown notification action:', action);
            console.log('NotificationBell: Available action data:', notification.action_data);
            // For unknown actions, provide a fallback navigation
            if (notification.type === 'private_message') {
              navigate('/profile/edit?tab=inbox');
            } else {
              // Generic fallback - go to profile
              navigate('/profile/edit');
            }
            break;
        }
        
        // Close dropdown after navigation
        setIsOpen(false);
      } else {
        console.log('NotificationBell: No action_data or action found:', notification.action_data);
        // Even without action_data, provide a reasonable navigation based on notification type
        if (notification.type === 'private_message') {
          navigate('/profile/edit?tab=inbox');
        } else {
          // Generic fallback
          navigate('/profile/edit');
        }
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error handling notification view:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Refresh notifications when opening
      fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setIsLoading(true);
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      // Notification should be removed from UI immediately due to optimistic updates
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Note: With optimistic updates, the notification is already removed from UI
      // even if the server request fails
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'private_message':
        return '💬';
      case 'circle_message':
        return '🔘';
      case 'circle_new_post':
        return '📝';
      case 'circle_new_member':
        return '👋';
      case 'new_follower':
        return '👤';
      case 'circle_update':
        return '📢';
      default:
        return '🔔';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'medium':
        return 'border-l-blue-500 bg-blue-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={handleToggleDropdown}
        className={`relative p-2 rounded-full transition-colors duration-200 ${
          isOpen 
            ? 'bg-green-100 text-green-700' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
        }`}
        aria-label="Notifications"
        title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-6 h-6" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        
        {/* Connection Status Indicator */}
        <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
          isConnected ? 'bg-green-500' : 'bg-gray-400'
        }`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={isLoading}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  title="Mark all as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  You'll see your messages, follows, and community updates here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                      notification.status === 'unread' 
                        ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                        : 'border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 text-2xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {notification.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center space-x-3 mt-2">
                              <span className="text-xs text-gray-500 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatTimeAgo(notification.created_at)}
                              </span>
                              {notification.priority === 'high' && (
                                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                                  High Priority
                                </span>
                              )}
                              {notification.priority === 'urgent' && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                                  Urgent
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="text-red-600 hover:text-red-800 p-1 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Action button - show for all notifications */}
                        <div className="mt-2">
                          <button 
                            onClick={() => handleNotificationView(notification)}
                            className="text-xs bg-green-600 text-white px-3 py-1 rounded-full hover:bg-green-700 transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationBell;